import React, { memo, useCallback, useMemo, useState } from "react"
import { useLocalization } from "../../../../hooks/localization"
import { useHistory, useLocation, useParams } from "react-router-dom"
import { useMessage } from "../../../../hooks/message"
import { useRPC } from "../../../../hooks/rpc"
import { useKeyboard } from "../../../../hooks/keyboard"
import { PasswordContainer } from "../../../../components/PasswordContainer"
import { SettingsPageContainer } from "../../../../components/Settings/SettingsPageContainer"
import { INPUT_TYPE_ID_KEYPAD, INPUT_TYPE_ID_SCENARIO, InputModel } from "../../../../types/control"
import { useSetSelector } from "../../../../hooks/set-selector"
import { useSelector } from "react-redux"
import { SettingInputComponent } from "../../../../components/Settings/SettingInputComponent"
import { SettingReadOnlyRowComponent } from "../../../../components/Settings/SettingReadOnlyRowComponent"
import { SettingSelectComponent } from "../../../../components/Settings/SettingSelectComponent"
import { SelectItem } from "../../../../components/Select"
import { isValidNumber } from "../../../../utils/validation"

export const InputEditScreen = memo(function KeypadEditScreen() {
  const { t } = useLocalization()
  const params = useParams<{ device_id: string; input_id: string }>()
  const { success, failure } = useMessage()
  const [rpc] = useRPC()
  const history = useHistory()

  const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
  const [setActiveNumpadInput, renderNumpadKeyboard] = useKeyboard("numpad", true)

  const location = useLocation();
  const controlList = useSelector(state => state.control.controlList)
  const controlSet = useSetSelector(state => state.control.controlList)
  const virtualSet = useSetSelector(state => state.control.virtualControlList)
  const virtualList = useSelector(state => state.control.virtualControlList)
  const inputList = useSelector(state => state.control.inputList)
  const controlTypeSet = useSetSelector(state => state.control.controlTypeList)
  const inputTypeList = useSelector(state => state.control.inputTypeList)
  const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList)
  const inputTypeSet = useSetSelector(state => state.control.inputTypeList)
  const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selected = useMemo(() => location.state?.input || inputList.find(control => control.id === params?.input_id) || null, [])

  const virtualControlName = virtualList
    .filter(virtual => controlSet[virtual?.control_id])
    .find(virtual => virtual?.id === selected?.relational_id)

  const controlName = controlList
    .filter(control => controlTypeSet[control?.type_id])
    .find(control => (selected.is_virtual_control ? virtualSet[virtualControlName?.id] : control?.id === selected?.relational_id))

  const inputName = inputTypeList
    .filter(input => inputTypeSet[input?.id])
    .find(input => input?.id === selected?.type_id)

  const deviceName = bridgeDeviceList
    .filter(bridgeDevice => bridgeDeviceTypeSet[bridgeDevice?.type_id])
    .find(bridgeDevice => bridgeDevice?.id === selected?.bridge_device_id)

  const DIRECTION_OPTION_LIST = useMemo<SelectItem<number>[]>(
    () => [
      {
        value: 0,
        label: t("close_stop")
      },
      {
        value: 1,
        label: t("open_up")
      },
      {
        value: 2,
        label: t("down")
      },
      {
        value: 3,
        label: t("open_close")
      }
    ],
    [t]
  )

  const [direction, setDirection] = useState<SelectItem<number>>(
    selected == null
      ? DIRECTION_OPTION_LIST[0]
      : DIRECTION_OPTION_LIST.find(option => option.value === selected.direction) || null
  )

  const [name, setName] = useState((selected?.id != null && selected?.name) || "")
  const [pinCode, setPinCode] = useState((selected?.keypad_settings != null && selected?.keypad_settings?.pin) || "")

  const onBackPressed = useCallback(() => history.push(`/settings/keypad/${params.device_id}/`), [history])

  const onClickSave = useCallback(() => {
    if (name.length === 0 || name == null) {
      failure(t("pls_enter_name"))
      return false
    }
    if (pinCode.length !== 4 || !isValidNumber(pinCode)) {
      failure(t("pls_enter_a_valid_pin_code"))
      return false
    }
    if (inputName?.id === INPUT_TYPE_ID_KEYPAD) {
      if (direction?.value === null) {
        failure(t("pls_select_a_direction"))
        return false
      }
    }
    const keypad = {
      input_id: selected?.id,
      name,
      direction: direction?.value,
      pin: pinCode
    }
    return rpc
      .updateKeypadInputSettings(keypad)
      .then(() => {
        success(t("keypad_settings_successfully_edited"))
        onBackPressed()
      })
      .catch(failure)
  }, [t, failure, success, onBackPressed, rpc, name, selected, direction, pinCode, inputName])

  const buttons = useMemo(
    () => [
      {
        text: t("save"),
        className: "green",
        onClick: onClickSave
      },
      {
        text: t("cancel"),
        className: "red",
        onClick: onBackPressed
      }
    ],
    [t, onBackPressed, onClickSave]
  )

  const onBlur = useCallback(() => {
    setActiveNumpadInput(null)
    setActiveInput(null)
  }, [setActiveInput, setActiveNumpadInput])

  return (
    <PasswordContainer requiredRole="user" isFullscreen={true}>
      <SettingsPageContainer
        buttons={buttons}
        className="service-edit"
        backUrl={`/settings/keypad/${params.device_id}/`}
        currentInput={currentInput}
      >
        <div className="content-divider">
          <div className="content-wrapper">
            <SettingInputComponent
              placeholder={t("write_input_name")}
              label={t("input_name")}
              value={name}
              setValue={setName}
              setActiveInput={setActiveInput}
              onBlur={onBlur}
            />
            <SettingReadOnlyRowComponent title={t("device")} value={deviceName?.name} />
            <SettingReadOnlyRowComponent title={t("input_type")} value={inputName?.name} />
            {renderIf(inputName?.id !== INPUT_TYPE_ID_SCENARIO)(() => (
              <SettingReadOnlyRowComponent title={t("control")} value={controlName?.name} />
            ))}
            {renderIf(inputName?.id !== INPUT_TYPE_ID_SCENARIO)(() => (
              <SettingSelectComponent
                label={t("direction")}
                options={DIRECTION_OPTION_LIST}
                onChange={setDirection}
                value={direction}
              />
            ))}

            <SettingInputComponent
              placeholder={t("write_pin_code")}
              label={t("pin_code")}
              value={pinCode}
              maxLength={4}
              setValue={setPinCode}
              setActiveInput={setActiveNumpadInput}
              onBlur={onBlur}
            />
          </div>
          <div className="content-wrapper"></div>
        </div>
      </SettingsPageContainer>
      {renderKeyboard()}
      {renderNumpadKeyboard()}
    </PasswordContainer>
  )
})